import {
  getNavigatorGeometryFault,
  getNavigatorState,
} from "../../../../../../modules/navigatorStateHelper";

export type PredicateVerdict = "success" | "failure-unexpected" | "failure-geometry" | "pending";

export type PredicateContext = {
  /** Milliseconds since the command was published. */
  msSinceRegistered: number;
};

/**
 * The org snapshot keeps the last Navigator/DebugString until the machine
 * publishes a new one, so a fault can be left over from an earlier attempt.
 * A real fault outlives this window; a stale one is overwritten within it.
 */
export const GEOMETRY_FAULT_CONFIRM_MS = 10_000;

export type MachineSlice = {
  VADC?: { autonomy?: { state?: number[] } };
  Navigator?: { NavigatorState?: unknown; DebugString?: unknown };
  TaskDispatcher?: { TaskList?: { tasks?: unknown } };
};

const VADC_STATE_STANDBY = 1;
const VADC_STATE_ACTIVE = 2;
const VADC_STATE_LOW_ENERGY = 3;
const VADC_STATE_INVALID = 255;

function getVadc(slice: MachineSlice): number {
  return Number(slice?.VADC?.autonomy?.state?.[0]);
}

function getNav(slice: MachineSlice): string | undefined {
  return getNavigatorState(slice?.Navigator?.NavigatorState);
}

function isUnexpectedVadc(vadc: number): boolean {
  return vadc === VADC_STATE_INVALID || vadc === VADC_STATE_LOW_ENERGY;
}

export function isStartAutonomySuccess(
  slice: MachineSlice,
  ctx: PredicateContext,
): PredicateVerdict {
  const vadc = getVadc(slice);
  if (getNavigatorGeometryFault(slice?.Navigator)) {
    // Stay pending rather than reporting success: VADC reads ACTIVE even when the
    // navigator cannot plan. Only fail once the fault has outlived a stale value.
    return ctx.msSinceRegistered >= GEOMETRY_FAULT_CONFIRM_MS ? "failure-geometry" : "pending";
  }
  if (vadc === VADC_STATE_ACTIVE) return "success";
  if (isUnexpectedVadc(vadc)) return "failure-unexpected";
  return "pending";
}

export function isPauseAutonomySuccess(slice: MachineSlice): PredicateVerdict {
  const vadc = getVadc(slice);
  if (getNav(slice) === "INTERRUPTED") return "success";
  if (isUnexpectedVadc(vadc)) return "failure-unexpected";
  return "pending";
}

// A running machine reports GENERATING_PATH far more often than ENGAGED, so
// resume is confirmed by the navigator leaving INTERRUPTED for any live state.
const NAVIGATOR_RUNNING_STATES = ["GENERATING_PATH", "READY", "ENGAGED"];

export function isResumeAutonomySuccess(slice: MachineSlice): PredicateVerdict {
  const vadc = getVadc(slice);
  const nav = getNav(slice);
  if (nav !== undefined && NAVIGATOR_RUNNING_STATES.includes(nav)) return "success";
  if (isUnexpectedVadc(vadc)) return "failure-unexpected";
  return "pending";
}

export function isQuitMissionSuccess(slice: MachineSlice): PredicateVerdict {
  const vadc = getVadc(slice);
  if (vadc === VADC_STATE_STANDBY) return "success";
  if (isUnexpectedVadc(vadc)) return "failure-unexpected";
  return "pending";
}

/**
 * Confirms the machine has no tasks queued. Used to gate Start Autonomy so a
 * mission is never dispatched on top of a stale task list. An absent TaskList
 * counts as cleared — machines drop the field entirely once it empties.
 */
export function isTaskListCleared(slice: MachineSlice): PredicateVerdict {
  const vadc = getVadc(slice);
  const tasks = slice?.TaskDispatcher?.TaskList?.tasks;
  if (!Array.isArray(tasks) || tasks.length === 0) return "success";
  if (isUnexpectedVadc(vadc)) return "failure-unexpected";
  return "pending";
}
